/* tslint:disable:max-line-length */
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable, Input, OnDestroy} from '@angular/core';
import {MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {Project} from '@docma-mm-core/project/project';
import {PageResult} from '@docma-mm-core/responces/page-result';
import {BasedataService} from '@docma-mm-core/shared/basedata.service';
import {User} from '@docma-mm-core/user/user';
import {deepEqual} from '@mydocma/common/src/lib/deep';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {Task} from '@mydocma/task';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, catchError} from 'rxjs';
import {MmReportTypeEnum} from '../../mm-report-type.enum';
import {MmFaultsReportingService} from '../mm-faults-reporting.service';
import {WritingEmailParameter} from './common/writing-email-parameter';
import {WritingOptions} from './common/writing-options';
import {WritingPreparation} from './common/writing-preparation';
import {WritingSendInfo} from './common/writing-send-info';
import {WritingTemplateData} from './common/writing-template';
import {WritingVariable, WritingVariableValue} from './common/writing-variable';
import {FaultsWritingDialogComponent} from './faults-writing-dialog/faults-writing-dialog.component';
import {FaultWritingReportConfig} from './faults-writing-report.config';
import {StepperService} from '@mydocma/import-export';
import {StepNextMethodEnum} from './faults-writing-stepper/steps/step-next-method.enum';
import {StepButtonTypeEnum} from './faults-writing-stepper/steps/step-button-type.enum';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {constants} from '@docma-mm-core/constants';
import {LlOptionEnum} from '../../ll-option.enum';
import {SettingsService} from '../../../mydocma-settings/settings.service';
import {SettingNamesEnum} from '../../../mydocma-settings/setting-names.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {MmReportTask} from '../../mm-report-task';
import { ConnectivityService } from '@mydocma/core';
import { PromptDialogService } from '@mydocma/component-library/src/lib/prompt-dialog';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class MmFaultsWritingReportingService extends BasedataService implements OnDestroy {

  private dialogRef: MatDialogRef<any>;

  nextSubject: BehaviorSubject<StepNextMethodEnum> = new BehaviorSubject<StepNextMethodEnum>(null);
  next$ = this.nextSubject.asObservable();

  @Input() steperService: StepperService;
  @Input() nextButtonText: string;
  @Input() emailButtonVisible = false;
  @Input() emailButtonDisabled = false;
  @Input() ButtonDefinitions: Map<StepButtonTypeEnum, { Method: StepNextMethodEnum, Args: any}>
                              = new Map<StepButtonTypeEnum, { Method: StepNextMethodEnum; Args: any}>();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  selectedTemplateSubject = new BehaviorSubject<WritingTemplateData>(null);
  selectedTemplate$ = this.selectedTemplateSubject.asObservable();

  constructor(private http: HttpClient,
              configService: ConfigService,
              private readonly translateService: TranslateService,
              private readonly notificationService: NotificationService,
              private readonly mmReportingService: MmFaultsReportingService,
              private readonly mmPersonalizationService: PersonalizationService,
              private readonly settingService: SettingsService,
              public dialog: MatDialog,
              private readonly connectivityService: ConnectivityService,
              private readonly dialogService: PromptDialogService,
              private readonly logger: Logger) {

    super(configService);

    this.ButtonDefinitions.set(StepButtonTypeEnum.Next, { Method: StepNextMethodEnum.Next, Args: null });
    this.ButtonDefinitions.set(StepButtonTypeEnum.Cancel, { Method: StepNextMethodEnum.None, Args: null });
    this.ButtonDefinitions.set(StepButtonTypeEnum.PreviewEmailBody, { Method: StepNextMethodEnum.None, Args: null });
  }

  ngOnDestroy(): void {

  }

  notifyLoadingState(loading: boolean) {
    this.loadingSubject.next(loading);
  }

  async cancelReportConfiguration(reportConfig: FaultWritingReportConfig) {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }

  async closeReportConfiguration(reportConfig: FaultWritingReportConfig) {
    if (this.dialogRef) {
      this.dialogRef.close(true);
    }
  }

  async startReportConfiguration(project: Project, faults: Array<FaultData>) {
    try {
      if (await this.connectivityService.isOfflineVal) {
        await this.dialogService.showOfflineNotificationDialog();
        return;
      }

      this.notifyLoadingState(false);
      const reportConfig: FaultWritingReportConfig = {
        project,
        faults
      };
      this.dialogRef = this.dialog.open(FaultsWritingDialogComponent, {
        data: {
          config: reportConfig, service: this
        },
        panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-writing-dialog'],
        autoFocus: false,
        disableClose: true });

      this.dialogRef.afterClosed().subscribe(async r => {
        if (!r) {
          if (reportConfig?.preparation) {
            try {
              await this.deleteWritingPreparation(reportConfig.preparation);
            } catch (err) {
              this.notificationService.warning(`Can't delete writing preparation ${reportConfig.preparation.FileGroupId}`);
            }
          }
        }
      });

    } catch (err) {
      this.logger.error(err, '', 'MmFaultsWritingReportingService');
      this.notificationService.error(this.translateService.instant('MM.reporting.reportGenError'));
    }
  }

  async getTemplates(projectId: number): Promise<WritingTemplateData[]> {
    const url = super.getFullUrl(`/projects/${projectId}/writings?$top=1000`);
    const response: PageResult<WritingTemplateData> = await this.http.get<any>(url).toPromise();
    if (response.Count === 0 || response.Data?.length === 0) {
      return [];
    }
    return response.Data;
  }


  async getUsers(projectId: number): Promise<User[]> {
    const url = super.getFullUrl(`/projects/${projectId}/users/search?` +
      `$top=${constants.MaxInt}&$skip=0&TerminatedAtIsNull=true&TerminatedToIsNull=true&DeactivatedAtIsNull=true`);
    const response: PageResult<any> = await this.http.get<any>(url).toPromise();
    if (response.Count === 0 || response.Data?.length === 0) {
      return [];
    }
    return response.Data;
  }

  patchWritingVariable(projectId: number, writingVariable: WritingVariable, patches: any[]) {
    const endpoint = super.getFullUrl(`/projects/${projectId}/writings/${writingVariable.TemplateId}/variables/${writingVariable.WritingVariableDeclarationId}`);
    return this.http
      .patch<any>(endpoint, JSON.stringify(patches), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  async getWritingVariables(projectId: number, template: WritingTemplateData, onlyShowInSignatureTab?: boolean): Promise<WritingVariable[]> {

    const url = super
      .getFullUrl(`/projects/${projectId}/writings/${this.getApiTemplateId(template)}/variables?$top=1000${(onlyShowInSignatureTab != null ? `&onlyShowInSignatureTab=${onlyShowInSignatureTab.toString()}` : '')}`);
    const response: PageResult<any> = await this.http.get<any>(url).toPromise();
    if (response.Count === 0 || response.Data?.length === 0) {
      return [];
    }
    return response.Data;
  }

  private getApiTemplateId(template) {
    return `${!template.Project ? 'global.' : 'project.'}${template.Id}`;
  }

  validateWriting(reportConfig: FaultWritingReportConfig) {
    const endpoint = super.getFullUrl(`/reporting/writing/validate`);

    return this.http
      .post<{ IsValid: boolean, ValidationLocalizationKey: string }>(endpoint,
        {
          FaultIds: reportConfig.faults.map(f => f.Id),
          AddressType: reportConfig.options.AddressType,
          ResponsibleCompanyId: reportConfig.options.ResponsibleCompanyId
        },
        { headers: this.getHeaders(), params: new HttpParams() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  async getPrepare(reportConfig: FaultWritingReportConfig,
                   isWritingVariablesValid: boolean,
                   isOptionsValid: boolean,
                   variables: WritingVariableValue[],
                   options: WritingOptions) {
    let valid = true;

    reportConfig.options = null;
    reportConfig.variables = [];

    if (valid) {
      valid = isWritingVariablesValid;
      if (isWritingVariablesValid) {
        reportConfig.variables = variables;
      }
    }

    if (valid) {
      valid = isOptionsValid;
      if (valid) {
        reportConfig.options = options;
      }
    }

    if (valid) {
      reportConfig.useAlternativeReportingApi = (await this.settingService.loadGlobal(PersonalizationKeysEnum.UseAlternativeReportingApi, SettingNamesEnum.UseAlternativeReportingApi, false) ?? false);

      let printerless: any;
      if (this.configService.getReportingConfig().printerless !== null) {
        printerless = this.configService.getReportingConfig().printerless === false ? 0 : 1;
      } else {
        printerless = reportConfig.useAlternativeReportingApi ? 0 : 1;
      }

      reportConfig.options.LlOptions = [
        {llOption: LlOptionEnum.Printerless, value: printerless},
      ];

      if (!!this.configService.getReportingConfig().rtfShareObjectsThreshold) {
        reportConfig.options.LlOptions.push({llOption: LlOptionEnum.RtfShareObjectsThreshold, value: (this.configService.getReportingConfig().rtfShareObjectsThreshold ?? 50)});
      }

      if (!!this.configService.getReportingConfig().virtualDeviceScalingOptions) {
        reportConfig.options.LlOptions.push({llOption: LlOptionEnum.VirtualDeviceScalingOptions, value: (this.configService.getReportingConfig().virtualDeviceScalingOptions ?? 600)});
      }


      reportConfig.options.LlExportOptions = [
        /* NOT EXISTING IN NEWER LL VERSION... */
        // {"llExportOption": LlExportOptionEnum.PdfFontMode, "value": "5"},
        // {"llExportOption": LlExportOptionEnum.PdfPdfAMode, "value": "0"},
      ];

      const needPreparationRefresh = this.needRefreshPreparation(reportConfig);
      if (!needPreparationRefresh) {

        // calculate if refresh preview needed
        if (reportConfig.preparation?.ExternalRecipients?.length > 0) {
          let paramChanged = false;
          if (!deepEqual(reportConfig.preparation.Options, reportConfig.options)) {
            reportConfig.preparation.Options = reportConfig.options;
            paramChanged = true;
          } else if (!deepEqual(reportConfig.preparation.Variables, reportConfig.variables)) {
            reportConfig.preparation.Variables = reportConfig.variables;
            paramChanged = true;
          }
          if (paramChanged) {
            reportConfig.preparation?.ExternalRecipients.forEach(er => er.PreviewFile = null);
          }
        }
      } else {
        try {
          this.notifyLoadingState(true);
          await this.prepareWriting(reportConfig);
        } catch (ex) {
          this.logger.error(ex, '', 'MmFaultsWritingReportingService');
          if (ex.status === 400) {
            this.notificationService.error(ex.error);
          } else {
            this.notificationService.error('An error while preparing writing');
          }
          valid = false;
        } finally {
          this.notifyLoadingState(false);
        }
      }
    }
    return {
      valid,
      error: '',
      data: {}
    };
  }

  private needRefreshPreparation(reportConfig: FaultWritingReportConfig): boolean {
    if (!reportConfig.preparation) {
      return true;
    }
    if (reportConfig.selectedTemplate.Id !== reportConfig.preparation.Template.Id) {
      return true;
    }
    if (reportConfig.options.AddressType !== reportConfig.preparation.Options.AddressType) {
      return true;
    }
    if (reportConfig.options.ResponsibleCompanyId !== reportConfig.preparation.Options.ResponsibleCompanyId) {
      return true;
    }
    return false;
  }

  async prepareWriting(reportConfig: FaultWritingReportConfig): Promise<WritingPreparation> {

    try {
      if (reportConfig.preparation !== undefined && reportConfig.preparation) {
        await this.deleteWritingPreparation(reportConfig.preparation);
        reportConfig.preparation = null;
      }
    } catch (err) {
      this.notificationService.warning(`Can't delete writing preparation ${reportConfig.preparation.FileGroupId}`);
    }

    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/prepare`);
    const response = await this.http.post<any>(url, {
      FaultIds: reportConfig.faults.map(f => f.Id),
      AddressType: reportConfig.options.AddressType,
      ResponsibleCompanyId: reportConfig.options.ResponsibleCompanyId,
      UseAlternativeReportingApi: reportConfig.useAlternativeReportingApi
    }).toPromise();

    const preparation: WritingPreparation = response;
    preparation.Template = { Id: reportConfig.selectedTemplate.Id, Project: reportConfig.selectedTemplate.Project };
    preparation.Options = reportConfig.options;
    preparation.Variables = reportConfig.variables;

    reportConfig.preparation = preparation;
    return;
  }

  async deleteWritingPreparation(preparation: WritingPreparation): Promise<any> {
    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(preparation.Template)}/prepare/${preparation.FileGroupId}`);
    const response = await this.http.delete(url).toPromise();
    return;
  }

  async printWriting(reportConfig: FaultWritingReportConfig): Promise<string> {

    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/print`);

    const printBody = {
      ... { FileGroupId: reportConfig.preparation.FileGroupId },
      ... reportConfig.preparation.Options,
      ... { WritingVariables: reportConfig.variables },
      ... { UseAlternativeReportingApi: reportConfig.useAlternativeReportingApi }
    };

    const response = await this.http.post<any>(url, printBody).toPromise();

    this.mmReportingService.registerReportTask({
      projectId: reportConfig.project.MyDocmaUid,
      taskId: response.TaskId,
      reportType: MmReportTypeEnum.writing,
      faults: reportConfig.faults,
      reports: ((response.ExternalReportIds?.length ?? 0) > 0 ? Object.fromEntries(response.ExternalReportIds.map(x => [x, null])) : null)
    });

    this.openMessageCenter();
    this.closeReportConfiguration(reportConfig);

    return null;
  }

  async sendWriting(reportConfig: FaultWritingReportConfig, emailParamater: WritingEmailParameter): Promise<string> {
    this.mmPersonalizationService.setProjectValue(reportConfig.project.MyDocmaUid, reportConfig.selectedTemplate.Id + '.LastSendTo', emailParamater.ReplyTo);
    this.mmPersonalizationService.setProjectValue(reportConfig.project.MyDocmaUid, reportConfig.selectedTemplate.Id + '.LastInternalTo', emailParamater.InternalTo);
    this.mmPersonalizationService.setProjectValue(reportConfig.project.MyDocmaUid, reportConfig.selectedTemplate.Id + '.LastInternalCc', emailParamater.InternalCc);
    this.mmPersonalizationService.setProjectValue(reportConfig.project.MyDocmaUid, reportConfig.selectedTemplate.Id + '.LastInternalBcc', emailParamater.InternalBcc);
    this.mmPersonalizationService.setProjectValue(reportConfig.project.MyDocmaUid, 'LastWritingCreationType', StepNextMethodEnum.Send);

    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/send`);

    const printBody = {
      ... { FileGroupId: reportConfig.preparation.FileGroupId },
      ... reportConfig.preparation.Options,
      ... { WritingVariables: reportConfig.preparation.Variables },
      ... { EmailParameter: emailParamater },
      ... { UseAlternativeReportingApi: reportConfig.useAlternativeReportingApi }
    };
    const response = await this.http.post<any>(url, printBody).toPromise();

    this.mmReportingService.registerReportTask({
      projectId: reportConfig.project.MyDocmaUid,
      taskId: response.TaskId,
      reportType: MmReportTypeEnum.writing,
      faults: reportConfig.faults,
      reports: ((response.ExternalReportIds?.length ?? 0) > 0 ? Object.fromEntries(response.ExternalReportIds.map(x => [x, null])) : null)
    } as MmReportTask);

    return null;
  }

  async previewWritingEmailBody(reportConfig: FaultWritingReportConfig, emailParamater: WritingEmailParameter) {
    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/prepare/sendinfo/emailbody`);

    const printBody = {
      ... { FileGroupId: reportConfig.preparation.FileGroupId },
      ... reportConfig.preparation.Options,
      ... { Freetext: emailParamater.FreeText },
      ... { IsFreetextVisible: reportConfig.preparation.SendInfo.IsFreetextVisible },
      ... { WritingVariables: reportConfig.preparation.Variables },
      ... { UseAlternativeReportingApi: reportConfig.useAlternativeReportingApi }
    };

    const response = await this.http.post<any>(url, printBody).toPromise();

    this.mmReportingService.registerReportTask({
      projectId: reportConfig.project.MyDocmaUid,
      taskId: response.TaskId,
      reportType: MmReportTypeEnum.writingPreviewEmailBody,
      reports: Object.fromEntries([response.ExternalReportId].map(x => [x, null]))
    } as MmReportTask);

    return { id: response.TaskId } as Task;
  }
  async previewWriting(rExternalId: string, reportConfig: FaultWritingReportConfig): Promise<Task> {
    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/preview/${rExternalId}`);

    const printBody = {
      ... { FileGroupId: reportConfig.preparation.FileGroupId },
      ... reportConfig.preparation.Options,
      ... { WritingVariables: reportConfig.preparation.Variables },
      ... { UseAlternativeReportingApi: reportConfig.useAlternativeReportingApi }
    };

    const response = await this.http.post<any>(url, printBody).toPromise();

    this.mmReportingService.registerReportTask({
      projectId: reportConfig.project.MyDocmaUid,
      taskId: response.TaskId,
      reportType: MmReportTypeEnum.writingPreview,
      reports: ((response.ExternalReportIds?.length ?? 0) > 0 ? Object.fromEntries(response.ExternalReportIds.map(x => [x, null])) : null)
    } as MmReportTask);

    return { id: response.TaskId } as Task;
  }


  async getWritingSendInfo(reportConfig: FaultWritingReportConfig): Promise<WritingSendInfo> {


    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/prepare/sendinfo`);
    const response = await this.http.post<WritingSendInfo>(url, {
      FileGroupId: reportConfig.preparation.FileGroupId,
      ActivityDate: reportConfig.options.ActivityDate,
      UserVariablesFromUserId: reportConfig.options.UserVariablesFromUserId
    }).toPromise();

    return response;

  }
  async uploadWritingAttachments(reportConfig: FaultWritingReportConfig, attachments: Array<File>): Promise<Array<string>> {

    const url = super
      .getFullUrl(`/reporting/writing/${this.getApiTemplateId(reportConfig.selectedTemplate)}/prepare/sendinfo/attachments`);

    const formData = new FormData();
    formData.append('FileGroupId', reportConfig.preparation.FileGroupId);
    for (const at of attachments) {
      formData.append('Files[]', at);
    }
    const response = await this.http.post<any>(url, formData).toPromise();

    return response;

  }

  openMessageCenter() {
    this.mmReportingService.openMessageCenter();
  }

}
